import { ssoLogin, checkEmailAvailability } from '@client/new/api/sso.api';
import { useUserStore } from '@client/new/stores/user.store';
import { useSsoSessionStore } from '@client/new/stores/sso-session.store';
import { useCallback, useEffect, useRef, useState } from 'react';
import { navigate } from '@client/routes';
import axios from 'axios';
import { trackSignInWithSso } from './register.effects';
import {
  GOOGLE_CLIENT_ID,
  buildGoogleOAuthUrl,
  openCenteredPopup,
} from '../google-oauth.utils';

type GoogleSsoState =
  | { type: 'idle' }
  | { type: 'loading' }
  | { type: 'error'; message: string };

export const useGoogleSso = (mode: 'login' | 'signup' = 'login') => {
  const [state, setState] = useState<GoogleSsoState>({ type: 'idle' });
  const processingRef = useRef(false);
  const popupRef = useRef<Window | null>(null);
  const oneTapHandledRef = useRef(false);

  const handleCredential = useCallback(
    async (credential: string) => {
      if (processingRef.current) {
        return;
      }

      processingRef.current = true;
      oneTapHandledRef.current = true;
      setState({ type: 'loading' });

      if (mode === 'signup') {
        try {
          const payload = JSON.parse(atob(credential.split('.')[1]));
          const email = payload.email;
          if (email) {
            const isAvailable = await checkEmailAvailability(email);
            if (!isAvailable) {
              const result = await ssoLogin({
                provider: 'google',
                idToken: credential,
              });
              const user = result?.user ?? result;
              trackSignInWithSso(user, 'google');
              useUserStore.getState().login(user);
              navigate('/');
              return;
            }
          }
        } catch {
          // availability check or login failed — fall through to registration
        }

        useSsoSessionStore.getState().setSsoSession('google', credential);
        setState({ type: 'idle' });
        processingRef.current = false;
        return;
      }

      try {
        const result = await ssoLogin({
          provider: 'google',
          idToken: credential,
        });

        const user = result?.user ?? result;
        trackSignInWithSso(user, 'google');
        useUserStore.getState().login(user);
        navigate('/');
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          useSsoSessionStore.getState().setSsoSession('google', credential);
          navigate('/sign-up');
          setState({ type: 'idle' });
          return;
        }

        setState({
          type: 'error',
          message: 'Google sign-in failed. Please try again.',
        });
      } finally {
        processingRef.current = false;
      }
    },
    [mode],
  );

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !window.google?.accounts?.id) {
      return;
    }

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (response) => handleCredential(response.credential),
    });

    window.google.accounts.id.prompt();
  }, [handleCredential]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data?.type === 'google-oauth-error') {
        popupRef.current?.close();
        popupRef.current = null;
        setState({
          type: 'error',
          message: 'Google sign-in failed. Please try again.',
        });
        return;
      }

      if (event.data?.type !== 'google-oauth-credential') {
        return;
      }

      popupRef.current?.close();
      popupRef.current = null;
      handleCredential(event.data.credential);
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [handleCredential]);

  const openOAuthPopup = useCallback(() => {
    const popup = openCenteredPopup(buildGoogleOAuthUrl(), 'google-oauth');
    if (!popup) {
      setState({
        type: 'error',
        message: 'Popup was blocked. Please allow popups and try again.',
      });
      return;
    }

    popupRef.current = popup;

    const pollTimer = setInterval(() => {
      if (!popup.closed) {
        return;
      }
      clearInterval(pollTimer);
      if (processingRef.current) {
        return;
      }
      setState({ type: 'idle' });
    }, 500);
  }, []);

  const initiateGoogleSignIn = useCallback(() => {
    if (!GOOGLE_CLIENT_ID) {
      setState({ type: 'error', message: 'Google Sign-In is not configured.' });
      return;
    }

    if (oneTapHandledRef.current) {
      return;
    }

    setState({ type: 'loading' });
    openOAuthPopup();
  }, [openOAuthPopup]);

  return {
    state,
    initiateGoogleSignIn,
  };
};
